import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Search } from 'lucide-react';
import { Loader } from '../../../components/ui';
import { calculateOptimalPopupPosition } from '../../../lib/utils';
import { supabase } from '../../../lib/supabase';
import { logger } from '../../../utils/logger';
import { useViewport } from '../../../contexts/ViewportContext';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { MobileFullScreenModal, ModalDoneFooter } from '../../../components/ui/MobileFullScreenModal';

interface ModelSelectorProps {
  onSelect: (modelId: string) => void;
  onClose: () => void;
  isOpen: boolean;
  triggerRef?: React.RefObject<HTMLElement>;
}

interface AIModel {
  id: string;
  name: string;
  model_id: string;
  is_active: boolean;
  model_type: string;
  description: string;
}

export function ModelSelector({ onSelect, onClose, isOpen, triggerRef }: ModelSelectorProps) {
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { isMobile } = useViewport();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside, with a delay to prevent race conditions
  const [outsideClickEnabled, setOutsideClickEnabled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Delay enabling outside click to avoid interference with dropdown click handlers
      const timer = setTimeout(() => {
        setOutsideClickEnabled(true);
      }, 100);
      return () => {
        clearTimeout(timer);
        setOutsideClickEnabled(false);
      };
    }
  }, [isOpen]);

  useOutsideClick(
    dropdownRef,
    event => {
      // Don't handle outside clicks on mobile - the MobileFullScreenModal has its own close button
      if (isMobile) {
        return;
      }
      
      // Check if the click event is on an element inside the dropdown
      const dropdownContent = document.querySelector('.model-selector-dropdown-content');
      if (
        dropdownContent &&
        event?.target instanceof Node &&
        dropdownContent.contains(event.target)
      ) {
        // Don't close if clicking inside the dropdown content
        return;
      }

      if (isOpen) onClose();
    },
    outsideClickEnabled
  );

  // Default model options if API fails
  const defaultModels = [
    { model_id: 'gpt-4-turbo-preview', name: 'GPT-4 Turbo' },
    { model_id: 'gpt-4-vision-preview', name: 'GPT-4 Vision' },
    { model_id: 'gpt-4', name: 'GPT-4' },
    { model_id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
  ];

  // Fetch available models from database
  useEffect(() => {
    async function fetchModels() {
      if (!isOpen) return; // Only fetch when selector is open

      try {
        setLoading(true);
        setError(null);

        // Get all models with a focus on chat models
        const { data, error } = await supabase.rpc('get_ai_model_configs');

        if (error) {
          logger.error('Error fetching AI models:', error);
          throw error;
        }

        // Filter for chat models
        const chatModels = data?.filter(model => model.model_type === 'chat') || [];
        setModels(chatModels);

        logger.ai('AI models loaded for selector', {
          count: chatModels.length,
          models: chatModels.map(m => m.model_id),
        });
      } catch (err) {
        logger.error('Failed to load AI models:', err);
        setError('Could not load models. Using default options.');

        // Use default models as fallback
        setModels(defaultModels as AIModel[]);
      } finally {
        setLoading(false);
      }
    }

    if (isOpen) {
      fetchModels();
    }
  }, [isOpen]);

  // Filter models based on search term
  const filteredModels = models.filter(model => {
    const modelIdMatches = model.model_id.toLowerCase().includes(searchTerm.toLowerCase());
    const nameMatches = model.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return modelIdMatches || nameMatches;
  });

  // Calculate dropdown position based on mobile status
  const getDropdownPosition = () => {
    if (isMobile) {
      // For mobile, center horizontally and position at 20% from top
      const viewportHeight = window.innerHeight;
      const topPosition = viewportHeight * 0.2; // 20% from top

      return {
        position: 'fixed',
        top: `${topPosition}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: '90%',
        width: '280px',
        zIndex: 100,
      };
    }

    // For desktop, calculate optimal position based on button
    if (!triggerRef?.current) return {};

    const buttonRect = triggerRef.current.getBoundingClientRect();

    // Default estimated height - adjust based on number of models
    const estimatedHeight = Math.min(300, filteredModels.length * 40 + 80);

    const position = calculateOptimalPopupPosition(
      { top: buttonRect.top, left: buttonRect.right }, // Position from top-right of button
      { width: 288, height: estimatedHeight },
      { x: -288, y: 5 } // Offset to align with right edge, slight y adjustment
    );

    return {
      position: 'fixed',
      top: `${position.top}px`,
      left: `${position.left}px`,
      zIndex: 100,
    };
  };

  if (!isOpen) return null;

  // For mobile, use the MobileFullScreenModal
  if (isMobile) {
    return ReactDOM.createPortal(
      <MobileFullScreenModal
        isOpen={isOpen}
        onClose={onClose}
        title="Select AI Model"
        footerContent={<ModalDoneFooter onClose={onClose} />}
      >
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search models..."
              className="w-full py-2 pl-9 pr-3 bg-gray-50 rounded-md focus:outline-none shadow-sm text-gray-700 placeholder-gray-500"
            />
            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
          </div>
        </div>

        <div className="overflow-y-auto model-selector-dropdown-content">
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <Loader />
            </div>
          ) : error ? (
            <div className="p-3 text-sm text-red-600 bg-red-50">{error}</div>
          ) : filteredModels.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No models found</div>
          ) : (
            <div className="py-1">
              {filteredModels.map(model => (
                <button
                  key={model.model_id}
                  onClick={e => {
                    e.stopPropagation();
                    onSelect(model.model_id);
                  }}
                  className="w-full px-4 py-4 text-left hover:bg-gray-100 flex items-center justify-between group border-b border-gray-100"
                >
                  <div>
                    <div className="font-medium text-gray-900">{model.name || model.model_id}</div>
                    {model.name && (
                      <div className="text-xs text-gray-500 font-mono mt-1">{model.model_id}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </MobileFullScreenModal>,
      document.body
    );
  }

  // For desktop, use the original dropdown
  return ReactDOM.createPortal(
    <>
      <div
        ref={dropdownRef}
        style={getDropdownPosition()}
        className="w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-[999] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-3 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search models..."
              className="w-full py-2 pl-9 pr-3 bg-gray-50 rounded-md focus:outline-none shadow-sm text-gray-700 placeholder-gray-500"
            />
            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
          </div>
        </div>

        <div className="max-h-60 overflow-y-auto model-selector-dropdown-content">
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <Loader />
            </div>
          ) : error ? (
            <div className="p-3 text-sm text-red-600 bg-red-50">{error}</div>
          ) : filteredModels.length === 0 ? (
            <div className="p-3 text-center text-gray-500">No models found</div>
          ) : (
            <div className="py-1">
              {filteredModels.map(model => (
                <button
                  key={model.model_id}
                  onClick={e => {
                    e.stopPropagation();
                    onSelect(model.model_id);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between group"
                >
                  <div>
                    <div className="font-medium text-gray-900">{model.name || model.model_id}</div>
                    {model.name && (
                      <div className="text-xs text-gray-500 font-mono">{model.model_id}</div>
                    )}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 text-xs bg-blue-500 text-white px-2 py-1 rounded">
                    Select
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
